import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import OnboardingShell, { BillingToggles, PlanFeatures, mark, useOnboardingStyles } from '../OnboardingShell';
import { SUBSCRIPTIONS_LINK } from '../../../common/urlLinks';
import { GA_EVENT_TREE } from '../../../gaConstants';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const useStyles = makeStyles(() => ({
    planName: {
        margin: '0 0 6px',
        fontSize: '1.4rem',
        letterSpacing: '-0.025em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    price: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '6px',
        margin: '0 0 4px',
    },
    amount: {
        fontSize: '2.6rem',
        lineHeight: 1,
        letterSpacing: '-0.04em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    unit: {
        color: REDESIGN_COLORS.body,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    note: {
        margin: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.88rem',
        fontWeight: 600,
    },
    sub: {
        margin: '4px 0 0',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
    },
    rule: {
        height: '1px',
        margin: '22px 0',
        background: REDESIGN_COLORS.sand,
    },
    total: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '12px',
        margin: '0 0 18px',
        fontSize: '1.05rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    totalAmount: {
        fontSize: '1.5rem',
        letterSpacing: '-0.03em',
    },
    payForm: {
        display: 'grid',
        gap: '12px',
    },
    wide: {
        width: '100%',
    },
}));

/**
 * The checkout step for Basic and Premium: billing period and currency,
 * what the plan includes, and the total, with payment handled by Stripe
 * through the existing checkout endpoint.
 */
const PaidStage = ({ subscriptions, state, pricing }) => {
    const classes = useOnboardingStyles();
    const own = useStyles();
    const intl = useIntl();
    const [billAnnual, setBillAnnual] = useState(true);
    const [billEur, setBillEur] = useState(true);

    useEffect(() => {
        setBillAnnual(state.billAnnual);
        setBillEur(state.billEur);
    }, [state]);

    useEffect(() => {
        if (state.subscription === 'BASIC') {
            window.logAppActivity(GA_EVENT_TREE.mainPage.onboarding.startBasic);
        }
        if (state.subscription === 'PREMIUM') {
            window.logAppActivity(GA_EVENT_TREE.mainPage.onboarding.startPremium);
        }
    }, [state.subscription]);

    if (!subscriptions || !state || !subscriptions[state.subscription]) {
        return null;
    }

    const list = pricing && pricing[state.subscription];
    const price = Array.isArray(list)
        ? list.find((p) => p.currency === (billEur ? 'eur' : 'ron') && p.interval === (billAnnual ? 'year' : 'month'))
        : null;
    if (!price) {
        return null;
    }

    const t = (id, values) => intl.formatMessage({ id: `redesign.home.pricing.${id}` }, values);
    const amount = price.unit_amount / 100;
    const money = (value) =>
        intl.formatNumber(value, { style: 'currency', currency: billEur ? 'EUR' : 'RON', maximumFractionDigits: 2 });
    const vat = intl
        .formatMessage({
            id:
                price.tax_behavior === 'exclusive'
                    ? 'subscriptions.subscription.includeVat'
                    : 'subscriptions.subscription.excludeVat',
        })
        .trim();
    const planName = intl.formatMessage({ id: `subscriptions.subscription.${state.subscription}.name` });

    const chooseAnnual = (annual) => {
        setBillAnnual(annual);
        window.logAppActivity(
            annual ? GA_EVENT_TREE.mainPage.onboarding.checkAnnual : GA_EVENT_TREE.mainPage.onboarding.checkMonthly
        );
    };
    const chooseEur = (eur) => {
        setBillEur(eur);
        window.logAppActivity(eur ? GA_EVENT_TREE.mainPage.onboarding.checkEur : GA_EVENT_TREE.mainPage.onboarding.checkRon);
    };

    return (
        <OnboardingShell
            title={<FormattedMessage id="redesign.onboarding.paid.title" values={{ plan: planName, mark }} />}
            lead={<FormattedMessage id="redesign.onboarding.paid.lead" />}
        >
            <div className={classes.layout}>
                <div className={classes.card}>
                    <BillingToggles isAnnual={billAnnual} isEur={billEur} onAnnual={chooseAnnual} onEur={chooseEur} />
                    <h2 className={own.planName}>{planName}</h2>
                    <div className={own.price}>
                        <span className={own.amount}>{intl.formatNumber(amount, { maximumFractionDigits: 2 })}</span>
                        <span className={own.unit}>
                            {billEur ? '€' : 'lei'} {billAnnual ? t('period.year') : t('period.month')}
                        </span>
                    </div>
                    <p className={own.note}>{(billAnnual ? t('billedAnnually') : t('billedMonthly')) + ' · ' + vat}</p>
                    <p className={own.sub}>
                        {billAnnual ? t('perMonthEquivalent', { amount: money(amount / 12) }) : t('cancelAnytime')}
                    </p>
                    <div aria-hidden="true" className={own.rule} />
                    <h3 className={classes.cardTitle}>
                        <FormattedMessage id="redesign.onboarding.paid.includes" />
                    </h3>
                    <PlanFeatures type={state.subscription} subscriptions={subscriptions} />
                </div>

                <aside className={classes.card}>
                    <div className={own.total}>
                        <span>
                            <FormattedMessage id="redesign.onboarding.paid.total" />
                        </span>
                        <span className={own.totalAmount}>{money(amount)}</span>
                    </div>
                    <form
                        className={own.payForm}
                        action={`/api/private/profile/checkout?subscriptionType=${state.subscription}&currency=${
                            billEur ? 'eur' : 'ron'
                        }&interval=${billAnnual ? 'year' : 'month'}`}
                        method="POST"
                    >
                        <button type="submit" className={`${classes.primary} ${own.wide}`}>
                            <FormattedMessage id="redesign.paidStage.pay" />
                            <span aria-hidden="true">→</span>
                        </button>
                        <Link to={SUBSCRIPTIONS_LINK} className={`${classes.secondary} ${own.wide}`}>
                            <FormattedMessage id="onboarding.chooseAnother" />
                        </Link>
                    </form>
                    <p className={classes.trust}>
                        <FormattedMessage id="redesign.onboarding.paid.secure" />
                    </p>
                </aside>
            </div>
        </OnboardingShell>
    );
};

const mapStateToProps = (state) => {
    const { mainAppState } = state;
    return { subscriptions: mainAppState.subscriptions, pricing: mainAppState.pricing };
};

export default connect(mapStateToProps)(PaidStage);
